import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import { palette } from '@app/palette';

// Falls past the bottom of any screen height and ends fully transparent; the animation holds its
// last frame (`forwards`), so anything still visible at 100% would be left sitting on the screen.
const fall = keyframes`
  0% { transform: translateY(-30px) rotate(0deg); opacity: 1; }
  80% { opacity: 0.6; }
  100% { transform: translateY(110vh) rotate(540deg); opacity: 0; }
`;

const CONFETTI_SHADES = [
  palette.primary.main,
  palette.second.main,
  palette.first.main,
  palette.primary.light,
  palette.error.light,
];

// Deterministic pieces (same spread as the prototype) so the animation is stable across renders.
const CONFETTI_PIECES = Array.from({ length: 42 }, (_, i) => {
  const size = 6 + ((i * 4) % 9);
  const square = i % 3 !== 0;
  return {
    left: (i * 41 + 7) % 100,
    delay: (i * 0.06) % 0.7,
    duration: 1.3 + ((i * 0.09) % 0.6),
    width: square ? size : size * 0.55,
    height: square ? size * 0.5 : size,
    color: CONFETTI_SHADES[i % CONFETTI_SHADES.length],
  };
});

/** CSS-only confetti that plays once on mount and leaves nothing behind; hidden for users who prefer reduced motion. */
export const Confetti: React.FC = () => (
  <Box
    aria-hidden
    sx={{
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      overflow: 'hidden',
      zIndex: 0,
      '@media (prefers-reduced-motion: reduce)': { display: 'none' },
    }}
  >
    {CONFETTI_PIECES.map((p, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          top: '-30px',
          left: `${p.left}%`,
          width: `${p.width}px`,
          height: `${p.height}px`,
          background: p.color,
          borderRadius: '2px',
          animation: `${fall} ${p.duration}s ease-in ${p.delay}s forwards`,
        }}
      />
    ))}
  </Box>
);
